import { JumpTarget } from 'config/loanJumpTargets'
import { defaultInputs as budgetInputs } from 'pages/Loan/Budget/constants'
import { defaultInputs as closingCalendarInputs } from 'pages/Loan/ClosingCalendar/constants'
import { defaultInputs as generateDocumentsInputs } from 'pages/Loan/ClosingScreen/GenerateDocuments/constants'
import { defaultInputs as closingLightningDocsInputs } from 'pages/Loan/ClosingScreen/LightningDocs/constant'
import { defaultInputs as contractorApplicationInputs } from 'pages/Loan/ContractorApplications/constants'
import { defaultInputs as documentGenieInputs } from 'pages/Loan/DocumentGenie/constants'
import { fundingDisbursementInputs } from 'pages/Loan/Funding/Disbursement/constant'
import { defaultInputs as loanIntakeDocumentInputs } from 'pages/Loan/LoanInTake/LoanIntakeDocument/constants'
import { defaultInputs as tridImportantDatesInputs } from 'pages/Loan/LoanInTake/TridImportantDates/constants'
import { defaultInputs as allongeInputs } from 'pages/Loan/PostClosing/Allonge/constants'
import { defaultInputs as assignmentInputs } from 'pages/Loan/PostClosing/Assignment/constants'
import { defaultInputs as loanTradeInputs } from 'pages/Loan/PostClosing/LoanTrade/constant'
import {
  purchaseAdviceAccounting as purchaseAdviceBuyerAccounting,
  purchaseAdviceGeneralInfos as purchaseAdviceBuyerGeneralInfos,
  purchaseAdvicePostClosing as purchaseAdviceBuyerPostClosing,
} from 'pages/Loan/PostClosing/PurchaseAdvice/constant'
import {
  purchaseAdviceAccounting as purchaseAdviceSellerAccounting,
  purchaseAdviceGeneralInfos as purchaseAdviceSellerGeneralInfos,
  purchaseAdvicePostClosing as purchaseAdviceSellerPostClosing,
  purchaseAdviceWireInstructions as purchaseAdviceSellerWireInstructions,
} from 'pages/Loan/PostClosing/PurchaseAdviceSeller/constant'
import { defaultInputs as servicingTransferInputs } from 'pages/Loan/PostClosing/ServicingTransfer/constants'
import { defaultInputs as shippingInformationInputs } from 'pages/Loan/PostClosing/ShippingInformation/constants'
import { defaultInputs as transferLetterInputs } from 'pages/Loan/PostClosing/TransferLetter/constants'
import {
  defaultInputs as dispositionStrategyInputs,
  defaultOfferInputs as dispositionOfferInputs,
} from 'pages/Loan/Reo/DispositionStrategy/constants'
import { defaultInputs as reoFinancialTrackingInputs } from 'pages/Loan/Reo/FinancialTracking/constants'
import { getDefaultInputs as improvementLedgerInputs } from 'pages/Loan/Reo/ImprovementLedger/constants'
import { defaultInputs as propertyExpenseInputs } from 'pages/Loan/Reo/PropertyExpense/constants'
import { defaultInputs as propertyProfileInputs } from 'pages/Loan/Reo/PropertyProfile/constants'
import {
  buyPricingFields,
  investorCommitmentFields,
  originatorLockFields,
  sellPricingFields,
} from 'pages/Loan/SecondaryMarketing/constant'
import { defaultInputs as appraisalInformationInputs } from 'pages/Loan/Underwriting/AppraisalInformation/constants'
import { defaultInputs as nyTransactionsInputs } from 'pages/Loan/Underwriting/NYTransactions/constants'
import { defaultInputs as orderOutsInputs } from 'pages/Loan/Underwriting/OrderOuts/constants'
import { defaultInputs as reviewFormInputs } from 'pages/Loan/Underwriting/ReviewForm/constants'
import { uwSummaryDefaultInputs } from 'pages/Loan/Underwriting/UnderwritingSummary/constants'
import { defaultInputs as additionalMemberInputs } from 'pages/LoanApplication/AdditionalMember/constants'
import { defaultInputs as assetInputs } from 'pages/LoanApplication/AssetInformation/constants'
import { defaultInputs as creditScoreInputs } from 'pages/LoanApplication/CreditScore/constants'
import { getGrossMonthlyIncomeInputs } from 'pages/LoanApplication/Employment/grossMonthlyIncome.constants'
import { getOtherIncomeInputs } from 'pages/LoanApplication/Employment/otherIncome.constants'
import { getPrimaryEmploymentInputDefs } from 'pages/LoanApplication/Employment/primaryEmployment.constants'
import { getSecondaryEmploymentListInputDefs } from 'pages/LoanApplication/Employment/secondaryEmploymentList.constants'
import {
  defaultInputs as liabilityInputs,
  expenseInputs as liabilityExpenseInputs,
} from 'pages/LoanApplication/Liaibilities/constants'
import { MldsDefaultInputs } from 'pages/LoanApplication/MLDS/constant'
import { giftsOrGrantsInputs } from 'pages/LoanApplication/PropertyInformation/giftsOrGrants.constants'
import { otherMortgageLoansInputs } from 'pages/LoanApplication/PropertyInformation/otherMortgageLoans.constants'
import {
  commercialInputs as signCommercialInputs,
  defaultInputs as signApplicationInputs,
} from 'pages/LoanApplication/SignApplication/constants'
import { defaultInputs as trackRecordInputs } from 'pages/LoanApplication/TrackRecord/constants'

type FieldMap = Record<string, any>

type OriginationSource = {
  /** Stable prefix for the generated target ids. */
  id: string
  group: string
  to: (loanNumber: string | number) => string
  build: () => FieldMap
}

const application = (menu: string) => (n: string | number) => `/loan_process/application/${n}?menu=${menu}`
const closingScreen = (menu: string) => (n: string | number) => `/closing_screen/${n}?menu=${menu}`
const loanInTake = (menu: string) => (n: string | number) => `/loanInTake/${n}?menu=${menu}`
const postClosing = (menu: string) => (n: string | number) => `/post_closing/${n}?menu=${menu}`
const reo = (menu: string) => (n: string | number) => `/reo/${n}?menu=${menu}`
const underwriting = (menu: string) => (n: string | number) => `/underwriting/${n}?menu=${menu}`

// Origination screens whose field labels are defined in a co-located constants file.
// Mirrors STATIC_SOURCES in servicingFieldTargets.ts so both sides of the loan are
// indexed by the lookup rather than only servicing.
const STATIC_SOURCES: OriginationSource[] = [
  // ---- Loan application sections not covered by getLoanInputFields() ----
  {
    id: 'app-employment',
    group: 'Employment and Income (Origination)',
    to: application('employment'),
    build: () => ({
      ...getPrimaryEmploymentInputDefs(),
      ...getSecondaryEmploymentListInputDefs(),
      ...getGrossMonthlyIncomeInputs({}),
      ...getOtherIncomeInputs({}),
    }),
  },
  {
    id: 'app-asset',
    group: 'Asset & Liability (Origination)',
    to: application('asset'),
    build: () => ({
      ...assetInputs('asset'),
      ...assetInputs('otherAsset'),
      ...liabilityInputs({}, {}),
      ...liabilityExpenseInputs(),
    }),
  },
  { id: 'app-track', group: 'Track Record & REO (Origination)', to: application('track'), build: () => trackRecordInputs() },
  { id: 'app-credit', group: 'Credit Score (Origination)', to: application('credit'), build: () => creditScoreInputs() },
  {
    id: 'app-additional-member',
    group: 'Additional Entity Members (Origination)',
    to: application('additionalMember'),
    build: () => additionalMemberInputs(),
  },
  { id: 'app-mlds', group: 'MLDS (Origination)', to: application('mlds'), build: () => MldsDefaultInputs() },
  {
    id: 'app-sign',
    group: 'Acknowledgement & Sign (Origination)',
    to: application('sign'),
    build: () => ({ ...signApplicationInputs(), ...signCommercialInputs() }),
  },
  {
    id: 'app-property-extras',
    group: 'Loan and Property Information (Origination)',
    to: application('property'),
    build: () => ({ ...giftsOrGrantsInputs, ...otherMortgageLoansInputs }),
  },

  // ---- Underwriting ----
  {
    id: 'uw-summary',
    group: 'Underwriting Summary (Origination)',
    to: (n) => `/underwriting_summary/${n}`,
    build: () => uwSummaryDefaultInputs(),
  },
  {
    id: 'uw-appraisal-information',
    group: 'Appraisal Information (Origination)',
    to: (n) => `/appraisal_information/${n}`,
    build: () => appraisalInformationInputs(),
  },
  {
    id: 'uw-review-form',
    group: 'Review Form (Origination)',
    to: (n) => `/review_form/${n}`,
    build: () => reviewFormInputs(),
  },
  {
    id: 'uw-ny-transactions',
    group: 'NY Transactions (Origination)',
    to: underwriting('nyTransactions'),
    build: () => nyTransactionsInputs(),
  },

  // ---- Loan In-Take ----
  {
    id: 'intake-documents',
    group: 'Loan Intake Documents (Origination)',
    to: loanInTake('loan_intake_documents'),
    build: () => loanIntakeDocumentInputs(),
  },
  {
    id: 'intake-trid',
    group: 'TRID Important Dates (Origination)',
    to: loanInTake('trid_important_dates'),
    build: () => tridImportantDatesInputs(),
  },
  { id: 'intake-order-outs', group: 'Order Outs (Origination)', to: loanInTake('orderOuts'), build: () => orderOutsInputs() },

  // ---- Standalone origination screens ----
  { id: 'budget', group: 'Budget & Scope (Origination)', to: (n) => `/budget/${n}`, build: () => budgetInputs() },
  {
    id: 'closing-calendar',
    group: 'Closing Calendar (Origination)',
    to: (n) => `/closing_calendar/${n}`,
    build: () => closingCalendarInputs(),
  },
  {
    id: 'closing-doc-gen',
    group: 'Document Generate (Origination)',
    to: closingScreen('docGen'),
    build: () => generateDocumentsInputs(),
  },
  {
    id: 'closing-lightning-docs',
    group: 'Lightning Docs (Origination)',
    to: closingScreen('lightningDocs'),
    build: () => closingLightningDocsInputs(),
  },
  {
    id: 'document-genie',
    group: 'Document Genie (Origination)',
    to: (n) => `/document_genie/${n}`,
    build: () => documentGenieInputs(),
  },
  {
    id: 'contractor-application',
    group: 'Contractor Application (Origination)',
    to: (n) => `/contractor_application/${n}`,
    build: () => contractorApplicationInputs(),
  },
  {
    id: 'secondary-marketing',
    group: 'Secondary Marketing (Origination)',
    to: (n) => `/secondary_marketing_screen/${n}`,
    build: () => ({
      ...originatorLockFields,
      ...investorCommitmentFields,
      buyPricing: buyPricingFields,
      sellPricing: sellPricingFields,
    }),
  },
  { id: 'funding', group: 'Funding (Origination)', to: (n) => `/funding/${n}`, build: () => fundingDisbursementInputs() },

  // ---- Post Closing ----
  { id: 'pc-allonge', group: 'Allonge (Origination)', to: postClosing('allonge'), build: () => allongeInputs() },
  { id: 'pc-assignment', group: 'Assignment (Origination)', to: postClosing('assignment'), build: () => assignmentInputs() },
  {
    id: 'pc-transfer-letter',
    group: 'Transfer Letter (Origination)',
    to: postClosing('transferLetter'),
    build: () => transferLetterInputs(),
  },
  {
    id: 'pc-servicing-transfer',
    group: 'Servicing Transfer (Origination)',
    to: postClosing('servicingTransfer'),
    build: () => servicingTransferInputs(),
  },
  {
    id: 'pc-shipping',
    group: 'Shipping Information (Origination)',
    to: postClosing('shipping'),
    build: () => shippingInformationInputs(),
  },
  { id: 'pc-loan-trade', group: 'Loan Trade (Origination)', to: postClosing('loanTrade'), build: () => loanTradeInputs() },
  {
    id: 'pc-purchase-advice-buyer',
    group: 'Purchase Advice Buyer (Origination)',
    to: (n) => `/purchase_advice/${n}`,
    build: () => ({
      ...purchaseAdviceBuyerGeneralInfos,
      ...purchaseAdviceBuyerAccounting,
      ...purchaseAdviceBuyerPostClosing,
    }),
  },
  {
    id: 'pc-purchase-advice-seller',
    group: 'Purchase Advice Seller (Origination)',
    to: (n) => `/purchase_advice_seller/${n}`,
    build: () => ({
      ...purchaseAdviceSellerGeneralInfos,
      ...purchaseAdviceSellerAccounting,
      ...purchaseAdviceSellerWireInstructions,
      ...purchaseAdviceSellerPostClosing,
    }),
  },

  // ---- REO ----
  { id: 'reo-property-profile', group: 'Property Profile (REO)', to: reo('propertyProfile'), build: () => propertyProfileInputs() },
  {
    id: 'reo-financial-tracking',
    group: 'Financial Tracking (REO)',
    to: reo('financialTracking'),
    build: () => reoFinancialTrackingInputs(),
  },
  {
    id: 'reo-disposition',
    group: 'Disposition Strategy (REO)',
    to: reo('dispositionStrategy'),
    build: () => ({ ...dispositionStrategyInputs(), ...dispositionOfferInputs() }),
  },
  { id: 'reo-property-expense', group: 'Property Expense (REO)', to: reo('propertyExpense'), build: () => propertyExpenseInputs() },
  {
    id: 'reo-improvement-ledger',
    group: 'Improvement Ledger (REO)',
    to: reo('improvementLedger'),
    build: () => improvementLedgerInputs(),
  },
]

/** True when a node looks like a single field definition rather than a group of them. */
const isFieldDef = (node: any) => !!node && typeof node === 'object' && typeof node.title === 'string'

/**
 * Field maps come in two shapes: flat (`{ key: field }`) and grouped
 * (`{ group: { key: field } }`, used by Secondary Marketing, Property Expense,
 * Document Genie, …). Walk both and collect every `[key, field]` pair.
 */
const collectFields = (node: any, out: Array<[string, any]>, depth = 0) => {
  if (!node || typeof node !== 'object' || depth > 3) return
  Object.keys(node).forEach((key) => {
    const value = node[key]
    if (!value || typeof value !== 'object') return
    if (isFieldDef(value)) out.push([key, value])
    else collectFields(value, out, depth + 1)
  })
}

const fieldsToTargets = (source: OriginationSource, fields: FieldMap, seen: Set<string>): JumpTarget[] => {
  const pairs: Array<[string, any]> = []
  collectFields(fields, pairs)

  const out: JumpTarget[] = []
  pairs.forEach(([key, field]) => {
    const title = String(field?.title || '').trim()
    if (!title || field?.inputType === 'alert') return
    const dedupeKey = `${source.id}:${title.toLowerCase()}`
    if (seen.has(dedupeKey)) return
    seen.add(dedupeKey)
    out.push({
      id: `of-${source.id}-${key}`,
      label: title,
      group: source.group,
      keywords: `origination field ${key}`,
      to: source.to,
    })
  })
  return out
}

/** Field labels from the statically-defined origination screens. */
export const getOriginationStaticFieldTargets = (): JumpTarget[] => {
  const seen = new Set<string>()
  const out: JumpTarget[] = []
  for (const source of STATIC_SOURCES) {
    let fields: FieldMap = {}
    try {
      fields = source.build() || {}
    } catch {
      continue
    }
    out.push(...fieldsToTargets(source, fields, seen))
  }
  return out
}

// Screens whose field definitions are built by the backend per loan (same pattern as
// Servicing Information). Each response carries an `inputs` map keyed by field.
export const ORIGINATION_DYNAMIC_SOURCES: Array<{ id: string; group: string; to: (n: string | number) => string }> = [
  { id: 'hmda', group: 'HMDA (Origination)', to: (n) => `/hmda/${n}` },
  { id: 'loanEstimate', group: 'Loan Estimate (Origination)', to: (n) => `/loanEstimate/${n}` },
  { id: 'creditDenial', group: 'Credit Denial (Origination)', to: (n) => `/creditDenial/${n}` },
]

/** Map one backend-defined screen response (`{ inputs }`) onto jump targets. */
export const originationDynamicResToTargets = (sourceId: string, res: any): JumpTarget[] => {
  const source = ORIGINATION_DYNAMIC_SOURCES.find((s) => s.id === sourceId)
  if (!source || !res) return []
  return fieldsToTargets({ ...source, build: () => ({}) }, res.inputs || {}, new Set<string>())
}
